import {
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { nanoid } from 'nanoid';

import { LogicalOperator } from '@models/FilterDefinition';
import { FilterGroup as FilterGroupType } from '@models/SearchFilter';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { defaultLogicalOperator, getNewFilterField } from '@utils/filterUtils';

import FilterDialogContext, { FilterDialogContextValues } from './context';

type FilterDialogProps = PropsWithChildren<{
  isOpen: boolean;
}>;

const FilterDialogContextProvider: FC<FilterDialogProps> = ({ isOpen, children }) => {
  const {
    searchQuery: { filter: initFilter },
  } = useSearchAndFilter();

  const [searchFilter, setSearchFilter] = useState<FilterGroupType>(initFilter);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isOpen) setSearchFilter(initFilter);
    // Timeout used to clear unsaved filters _after_ dialog is closed
    else timer = setTimeout(() => setSearchFilter(initFilter), 500);
    return () => timer && clearTimeout(timer);
  }, [isOpen, initFilter]);

  const getHasRootLevelGroups = (filterGroup: FilterGroupType) =>
    filterGroup.fields.length > 1 &&
    filterGroup.fields.every(field => field.filter?.isRootLevelFilter);

  const hasRootLevelGroups = getHasRootLevelGroups(searchFilter);

  const addRootLevelFilterGroup = () =>
    setSearchFilter(prev => {
      const hasMultipleRootLevelGroups = getHasRootLevelGroups(prev);

      if (hasMultipleRootLevelGroups) {
        const tmp: FilterGroupType = {
          ...prev,
          fields: [...prev.fields, getNewFilterField(true)],
        };

        return tmp;
      }

      const tmp: FilterGroupType = {
        fields: [{ id: nanoid(6), filter: prev }, getNewFilterField(true)],
        logicalOperator: defaultLogicalOperator,
        isRootLevelFilter: true,
      };

      return tmp;
    });

  const handleRemoveFilter = (id: string) => () =>
    setSearchFilter(prev => {
      const tmp = prev.fields.filter(f => f.id !== id);
      if (tmp.length === 1 && tmp[0].filter?.isRootLevelFilter) return tmp[0].filter;
      else return { ...prev, fields: tmp };
    });

  const handleCombinatorChange = (logicalOperator: LogicalOperator) =>
    setSearchFilter(prev => ({ ...prev, logicalOperator }));

  const nestedFilterListChangeHandler = useCallback(
    (filterFieldId: string): Dispatch<SetStateAction<FilterGroupType>> =>
      arg =>
        setSearchFilter(prev => ({
          ...prev,
          fields: prev.fields?.map(filterField => {
            if (filterField.id === filterFieldId) {
              if (typeof arg === 'function') {
                if (filterField.filter) return { ...filterField, filter: arg(filterField.filter) };
              } else return { ...filterField, filter: arg };
            }

            return filterField;
          }),
        })),
    []
  );

  const values: FilterDialogContextValues = {
    addRootLevelFilterGroup,
    handleCombinatorChange,
    handleRemoveFilter,
    hasRootLevelGroups,
    nestedFilterListChangeHandler,
    searchFilter,
    setSearchFilter,
  };

  return <FilterDialogContext.Provider value={values}>{children}</FilterDialogContext.Provider>;
};

export default FilterDialogContextProvider;
